<template>
  <tr class="flex w-full py-2">
    <!-- Name -->
    <td class="w-3/12 flex items-center"
      @mouseenter="isNameHovered = true"
      @mouseleave="isNameHovered = false"
    >
      <div class="relative flex items-center w-full pl-2"
        :class="{'pr-4': isAddMode}"
      >
        <template v-if="isAddMode">
          <base-input v-model="editName"
            ref="nameInput"
            :variant="nameError ? 'table-cell-error' : 'table-cell'"
            class="w-full h-10"
            @keydown.esc.native="onCancel"
            @keydown.enter.native="focusValueInput"
          />
          <input-error-message :show-error-message="!!nameError" :error-message="nameError"/>
        </template>
        <template v-else-if="editingField === 'name'">
          <input-with-error-message
            :key="`name-${variable.id}-${inlineEditKey}`"
            :id="`variable-name-${variable.id}`"
            :value.sync="inlineEditName"
            :is-loading="isLoading"
            placeholder="Variable name"
            :error-message="nameError || 'Name cannot be empty'"
            class="w-full h-10"
            :show-cancel-button="true"
            @escape="onInlineNameEscape"
            @submit="onInlineNameSubmit"
          />
        </template>
        <template v-else>
          <div
            class="w-full flex items-center border border-transparent rounded border-solid cursor-text"
            :class="{'hover:border-gray-border': !isCopyNameHovered}"
            @click="openNameEdit"
          >
            <div class="relative flex min-w-0 items-center">
              <span
                class="absolute left-0 text-lg text-gray mr-4"
                :class="isCopyNameHovered ? 'opacity-100' : 'opacity-0'"
              >${</span>
              <span class="text-lg truncate pl-5">{{ variable.name }}</span>
              <span
                class="ml-0.5 flex-shrink-0 text-lg text-gray"
                :class="isCopyNameHovered ? 'opacity-100' : 'opacity-0'"
              >}</span>
            </div>
            <span v-if="isNameJustCopied" class="ml-3 pt-0.5 text-sm text-gray-dark">Path copied!</span>
            <div v-if="isNameHovered && !isNameJustCopied" class="ml-3 flex items-center">
              <base-button
                variant="transparent-gray"
                @mouseenter="isCopyNameHovered = true"
                @mouseleave="isCopyNameHovered = false"
                @click.stop="copyName"
              >
                <copy-input-icon class="text-gray-dark cursor-pointer"/>
              </base-button>
            </div>
          </div>
        </template>
      </div>
    </td>

    <!-- Value -->
    <td class="w-5/12 flex items-center"
      @mouseenter="isValueHovered = true"
      @mouseleave="isValueHovered = false"
    >
      <div class="relative flex items-center w-full"
        :class="{'pr-4': isAddMode}"
      >
        <template v-if="isAddMode">
          <base-input v-model="editValue"
            ref="valueInput"
            :variant="valueError ? 'table-cell-error' : 'table-cell'"
            class="w-full h-10"
            @keydown.esc="onCancel"
          />
          <input-error-message :show-error-message="!!valueError" :error-message="valueError"/>
        </template>
        <template v-else-if="editingField === 'value'">
          <input-with-error-message
            :key="`value-${variable.id}-${inlineEditKey}`"
            :id="`variable-value-${variable.id}`"
            :value.sync="inlineEditValue"
            :is-loading="isLoading"
            placeholder="Variable value"
            :error-message="valueError || 'Value cannot be empty'"
            class="w-full h-10"
            :show-cancel-button="true"
            @escape="onInlineValueEscape"
            @submit="onInlineValueSubmit"
          />
        </template>
        <template v-else>
          <div
            class="w-full flex items-center border border-transparent rounded border-solid cursor-text"
            :class="{'hover:border-gray-border': !isCopyValueHovered}"
            @click="openValueEdit"
          >
            <span class="text-lg truncate pl-3">{{ variable.value }}</span>
            <span v-if="isValueJustCopied" class="ml-3 pt-0.5 text-sm text-gray-dark">Value copied!</span>
            <base-button v-if="isValueHovered && !isValueJustCopied"
              variant="transparent-gray"
              class="ml-3"
              @mouseenter="isCopyValueHovered = true"
              @mouseleave="isCopyValueHovered = false"
              @click.stop="copyValue"
            >
              <copy-input-icon class="text-gray-dark cursor-pointer"/>
            </base-button>
          </div>
        </template>
      </div>
    </td>

    <!-- Scope -->
    <td class="w-3/12 flex items-center h-10"
      :class="{'pr-4': isAddMode}"
    >
      <template v-if="isAddMode">
        <base-select v-model="editScopeValue"
          :options="scopeValueOptions"
          value-attribute="value"
          text-attribute="label"
          class="w-full h-full"
          variant="baseSelect"
        />
      </template>
      <span v-else class="text-lg truncate pl-3">{{ scopeLabel }}</span>
    </td>

    <!-- Actions -->
    <td class="w-1/12 flex items-center gap-1 pr-3">
      <template v-if="isAddMode">
        <vx-tooltip
          text="Save"
          position="bottom"
          class="flex items-center justify-center h-10 w-10 rounded-full cursor-pointer bg-success hover:opacity-80"
          @click.native="onConfirm"
        >
          <checkmark-icon class="h-5 w-5 text-white"/>
        </vx-tooltip>
        <vx-tooltip
          text="Cancel"
          position="bottom"
          class="flex items-center justify-center h-10 w-10 rounded-full cursor-pointer bg-focus-error hover:opacity-80"
          @click.native="onCancel"
        >
          <close-icon class="h-5 w-5 text-white"/>
        </vx-tooltip>
      </template>

      <template v-else>
        <vx-tooltip v-if="variable.id !== ITEM_IDS.NEW"
          text="View Variable Usages"
          position="bottom"
          class="flex items-center justify-center h-10 w-10 rounded-full cursor-pointer text-gray-dark bg-soft hover:opacity-80"
          @click.native="$emit('view-variable-usages', variable.id)"
        >
          <eye-icon class="h-5 w-5"/>
        </vx-tooltip>
        <vx-tooltip text="Delete variable"
          position="bottom"
          class="flex items-center justify-center h-10 w-10 rounded-full cursor-pointer text-focus-error bg-focus-error-light hover:opacity-80"
          @click.native="onDelete"
        >
          <delete-icon class="h-5 w-5"/>
        </vx-tooltip>
      </template>
    </td>
  </tr>
</template>

<script>
import variablesModule from '@/store/modules/variables.module'
import variablesService from '@/services/amio/variables.service'
import botModule from '@/store/modules/bot.module'
import UserProfileService from '@/services/amio/user-profile.service'
import BaseInput from '@/components/base-components/BaseInput.vue'
import BaseButton from '@/components/base-components/BaseButton.vue'
import BaseSelect from '@/components/base-components/BaseSelect.vue'
import CopyInputIcon from '@/assets/images/icons/CopyInputIcon.vue'
import EyeIcon from '@/assets/images/icons/EyeIcon.vue'
import DeleteIcon from '@/assets/images/icons/DeleteIcon.vue'
import CheckmarkIcon from '@/assets/images/icons/CheckmarkIcon.vue'
import CloseIcon from '@/assets/images/icons/CloseIcon.vue'
import InputErrorMessage from '@/components/base-components/InputErrorMessage.vue'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import { ITEM_IDS } from '@/utils/editor.constants'
import { VARIABLE_COLS } from '@/store/constants'
import errorUtils from '@/utils/error.utils'
import toaster from '@/utils/toaster'

export default {
  name: 'VariablesTableRow',

  components: {
    BaseInput,
    BaseButton,
    BaseSelect,
    CopyInputIcon,
    EyeIcon,
    DeleteIcon,
    CheckmarkIcon,
    CloseIcon,
    InputErrorMessage,
    InputWithErrorMessage
  },

  props: {
    variable: {
      type: Object,
      required: true
    },
    storeIndex: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      isNameHovered: false,
      isValueHovered: false,
      isCopyNameHovered: false,
      isCopyValueHovered: false,
      isNameJustCopied: false,
      isValueJustCopied: false,
      copyFeedbackTimeout: null,
      inlineEditKey: 0,
      editingField: null,
      inlineEditName: '',
      inlineEditValue: '',
      editName: '',
      editValue: '',
      editScope: '',
      editScopeValue: '',
      ITEM_IDS
    }
  },

  computed: {
    isLoading: variablesModule.variablesLoading,
    bots: botModule.getBotList,
    organizationId: () => UserProfileService.getOrganizationId(),

    editedId() {
      return variablesModule.editedId.get()
    },

    variablesList() {
      return variablesModule.variables.get()
    },
    
    isAddMode() {
      return this.variable.id === ITEM_IDS.NEW
    },

    isEditMode() {
      return !this.isAddMode && this.editedId === this.variable.id
    },

    scopeLabel() {
      return this.variable.scope === 'global' ?
        'Global' :
        this.bots.find(bot => bot.id === this.variable.scope_value)?.name
    },

    scopeValueOptions() {
      const options = [{ label: 'Global', value: this.organizationId }]
      this.bots.forEach(bot => {
        options.push({ label: bot.name, value: bot.id })
      })
      return options
    },

    nameError: {
      get() {
        return variablesModule.variableError.get(this.variable.id, VARIABLE_COLS.NAME)
      },
      set(val) {
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.NAME, val)
      }
    },

    valueError: {
      get() {
        return variablesModule.variableError.get(this.variable.id, VARIABLE_COLS.VALUE)
      },
      set(val) {
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.VALUE, val)
      }
    }
  },

  watch: {
    'variable.id': {
      handler() {
        this.syncEditState()
      }
    },
    isEditMode: {
      immediate: true,
      handler(active) {
        if (active) this.syncEditState()
      }
    },
    isAddMode: {
      immediate: true,
      handler(active) {
        if (active) {
          this.syncEditState()
          this.$nextTick(() => this.focusNameInput())
        }
      }
    }
  },

  methods: {
    syncEditState() {
      this.editName = this.variable.name || ''
      this.editValue = this.variable.value || ''
      this.editScope = this.variable.scope || 'global'
      this.editScopeValue = this.editScope === 'global' ?
        this.organizationId :
        this.variable.scope_value || ''
    },

    copyName() {
      const text = `\${variable.${this.variable.name}}`
      this.$copyText(text).then(() => {
        this.isCopyNameHovered = false
        this.isCopyValueHovered = false
        this.isValueJustCopied = false
        this.isNameJustCopied = true
        if (this.copyFeedbackTimeout) clearTimeout(this.copyFeedbackTimeout)
        this.copyFeedbackTimeout = setTimeout(() => {
          this.isNameJustCopied = false
          this.copyFeedbackTimeout = null
        }, 3000)
      })
    },

    copyValue() {
      const text = String(this.variable.value || '')
      this.$copyText(text).then(() => {
        this.isCopyNameHovered = false
        this.isCopyValueHovered = false
        this.isNameJustCopied = false
        this.isValueJustCopied = true
        if (this.copyFeedbackTimeout) clearTimeout(this.copyFeedbackTimeout)
        this.copyFeedbackTimeout = setTimeout(() => {
          this.isValueJustCopied = false
          this.copyFeedbackTimeout = null
        }, 3000)
      })
    },

    openNameEdit() {
      this.inlineEditName = this.variable.name || ''
      this.inlineEditKey += 1
      this.editingField = 'name'
    },

    openValueEdit() {
      this.inlineEditValue = this.variable.value != null ? String(this.variable.value) : ''
      this.inlineEditKey += 1
      this.editingField = 'value'
    },

    onInlineNameEscape() {
      this.inlineEditName = this.variable.name || ''
      this.editingField = null
      this.nameError = null
    },

    onInlineValueEscape() {
      this.inlineEditValue = this.variable.value != null ? String(this.variable.value) : ''
      this.editingField = null
      this.valueError = null
    },

    async onInlineNameSubmit(newName) {
      if (newName === this.variable.name) {
        this.editingField = null
        return
      }

      await this.updateVariableWithPayload({ name: newName })
      if (!variablesModule.variableError.get(this.variable.id, VARIABLE_COLS.NAME)) {
        this.editingField = null
      }
    },

    async onInlineValueSubmit(newValue) {
      if (newValue === this.variable.value) {
        this.editingField = null
        return
      }

      await this.updateVariableWithPayload({ value: newValue })
      if (!variablesModule.variableError.get(this.variable.id, VARIABLE_COLS.VALUE)) {
        this.editingField = null
      }
    },

    focusNameInput() {
      const ref = this.$refs.nameInput
      if (ref && ref.$el) ref.$el.focus()
    },

    focusValueInput() {
      const ref = this.$refs.valueInput
      if (ref && ref.$el) ref.$el.focus()
    },

    onConfirm() {
      if (this.isAddMode) {
        this.createVariable()
      }
    },

    async createVariable() {
      this.nameError = null
      this.valueError = null
      try {
        variablesModule.variablesLoading.set(true)
        const payload = {
          name: this.editName.trim(),
          value: this.editValue.trim(),
          scope: this.editScopeValue === this.organizationId ? 'global' : 'bot'
        }
        if (payload.scope === 'bot') payload.scope_value = this.editScopeValue
        const newVariable = await variablesService.create(payload)
        variablesModule.setVariable(0, newVariable)
        variablesModule.editedId.set(null)
      } catch (err) {
        const msg = errorUtils.resolveMessage(err)
        this.nameError = msg
        toaster.error(msg)
      } finally {
        variablesModule.variablesLoading.set(false)
      }
    },

    async updateVariableWithPayload(payload) {
      const hasName = 'name' in payload
      const hasValue = 'value' in payload
      if (hasName) this.nameError = null
      if (hasValue) this.valueError = null
      try {
        variablesModule.variablesLoading.set(true)
        const body = { ...payload }
        if (typeof body.name === 'string') body.name = body.name.trim()
        if (typeof body.value === 'string') body.value = body.value.trim()
        const updated = await variablesService.update(this.variable.id, body)
        variablesModule.setVariable(this.storeIndex, updated)
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.NAME, null)
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.VALUE, null)
      } catch (err) {
        const data = err.response && err.response.data
        const code = data && data.code
        const isValidation = code && typeof code.includes === 'function' && code.includes('validation')
        const msg = errorUtils.resolveMessage(err)
        const desc = (data && data.details && data.details.description) || msg
        const errorMsg = isValidation ? desc : msg
        if (hasName) this.nameError = errorMsg
        if (hasValue) this.valueError = errorMsg
        toaster.error(errorMsg)
      } finally {
        variablesModule.variablesLoading.set(false)
      }
    },

    onCancel() {
      if (this.isAddMode) {
        const list = this.variablesList.filter(v => v.id !== ITEM_IDS.NEW)
        variablesModule.variables.set(list)
      }
      variablesModule.editedId.set(null)
      this.nameError = null
      this.valueError = null
      this.syncEditState()
    },

    async onDelete() {
      if (this.variable.id === ITEM_IDS.NEW) {
        const list = this.variablesList.filter(v => v.id !== ITEM_IDS.NEW)
        variablesModule.variables.set(list)
        variablesModule.editedId.set(null)
        return
      }
      try {
        variablesModule.variablesLoading.set(true)
        await variablesService.delete(this.variable.id)
        const list = this.variablesList.filter(v => v.id !== this.variable.id)
        variablesModule.variables.set(list)
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.NAME, null)
        variablesModule.variableError.set(this.variable.id, VARIABLE_COLS.VALUE, null)
      } catch (err) {
        const msg = errorUtils.resolveMessage(err)
        toaster.error(msg)
        if (msg.includes('used')) {
          this.$emit('view-variable-usages', this.variable.id)
        }
      } finally {
        variablesModule.variablesLoading.set(false)
      }
    }
  }
}
</script>
